import React, { useCallback, useEffect, useRef } from 'react';
import { EditorContent, type Editor, useEditor } from '@tiptap/react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Selection, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import StarterKit from '@tiptap/starter-kit';
import { replaceEditorContent } from '@/utils/replaceEditorContent';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { cn } from '@/lib/utils';
import { countWordsForProgress } from '@/utils/wordProgress';
import ListItemCycleShortcut from '@/components/extensions/ListItemCycleShortcut';
import {
  findManuscriptAnalysisMatches,
  type ManuscriptAnalysisConfig,
} from '@/utils/manuscriptAnalysis';

interface MarkdownShortcutNotesEditorProps {
  value: string;
  onChange: (value: string) => void;
  immediateChanges?: boolean;
  placeholder?: string;
  className?: string;
  minHeightClassName?: string;
  disabled?: boolean;
  documentKey?: string | null;
  quoteStyle?: 'straight' | 'german';
  typewriterMode?: 'off' | 'variable' | 'centered';
  onSelectionWordCountChange?: (wordCount: number) => void;
  analysisConfig?: ManuscriptAnalysisConfig | null;
  registerPendingChangeFlush?: (flush: (() => void) | null) => void;
  resizable?: boolean;
  containerRef?: React.Ref<HTMLDivElement>;
  style?: React.CSSProperties;
  initialPosition?: {
    cursor: number;
    scrollTop: number;
  } | null;
  onPositionChange?: (position: { cursor: number; scrollTop: number }) => void;
}

const QuickNotesTaskItem = TaskItem.extend({
  addInputRules() {
    return [];
  },
});

const findEditorScrollContainer = (node: HTMLElement): HTMLElement => {
  let current: HTMLElement | null = node;
  while (current) {
    const styles = window.getComputedStyle(current);
    const canScrollY = /(auto|scroll|overlay)/.test(styles.overflowY);
    if (canScrollY && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return node;
};

const buildIgnoredWordCountDecorations = (doc: Parameters<typeof DecorationSet.create>[0]) => {
  const decorations: Decoration[] = [];
  doc.descendants((node, position) => {
    if (!node.isTextblock) return true;
    const text = node.textContent;
    const matches = text.matchAll(/\+\+[\s\S]*?\+\+/g);
    for (const match of matches) {
      if (typeof match.index !== 'number') continue;
      decorations.push(
        Decoration.inline(
          position + 1 + match.index,
          position + 1 + match.index + match[0].length,
          { class: 'ns-ignored-word-count-range' }
        )
      );
    }
    return true;
  });
  return DecorationSet.create(doc, decorations);
};

const buildAnalysisDecorations = (
  doc: Parameters<typeof DecorationSet.create>[0],
  config: ManuscriptAnalysisConfig
) => {
  const decorations: Decoration[] = [];
  doc.descendants((node, position) => {
    if (!node.isText || !node.text) return true;
    findManuscriptAnalysisMatches(node.text, config).forEach((match) => {
      decorations.push(
        Decoration.inline(position + match.start, position + match.end, {
          class: `ns-analysis-highlight ns-analysis-${match.category}`,
        })
      );
    });
    return true;
  });
  return DecorationSet.create(doc, decorations);
};

const looksLikeHtml = (value: string): boolean => /<\/?[a-z][\s\S]*>/i.test(value);

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const markdownToEditorHtml = (raw: string): string => {
  const text = raw.trim();
  if (!text) return '<p></p>';

  const lines = raw.replace(/\r/g, '').split('\n');
  const html: string[] = [];
  let paragraphLines: string[] = [];
  let bulletItems: string[] = [];
  let numberedItems: string[] = [];
  let taskItems: Array<{ checked: boolean; value: string }> = [];
  let pendingBlankLines = 0;

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    html.push(`<p>${paragraphLines.map((line) => escapeHtml(line)).join('<br />')}</p>`);
    paragraphLines = [];
  };

  const flushBullets = () => {
    if (bulletItems.length === 0) return;
    html.push(
      `<ul>${bulletItems
        .map((item) => `<li><p>${escapeHtml(item)}</p></li>`)
        .join('')}</ul>`
    );
    bulletItems = [];
  };

  const flushNumbered = () => {
    if (numberedItems.length === 0) return;
    html.push(
      `<ol>${numberedItems
        .map((item) => `<li><p>${escapeHtml(item)}</p></li>`)
        .join('')}</ol>`
    );
    numberedItems = [];
  };

  const flushTasks = () => {
    if (taskItems.length === 0) return;
    html.push(
      `<ul data-type="taskList">${taskItems
        .map(
          (item) =>
            `<li data-type="taskItem" data-checked="${item.checked ? 'true' : 'false'}"><p>${escapeHtml(item.value)}</p></li>`
        )
        .join('')}</ul>`
    );
    taskItems = [];
  };

  const flushAll = () => {
    flushParagraph();
    flushBullets();
    flushNumbered();
    flushTasks();
  };

  const flushPendingBlankParagraphs = () => {
    if (pendingBlankLines === 0) return;
    const emptyParagraphs = Math.floor(pendingBlankLines / 2);
    for (let index = 0; index < emptyParagraphs; index += 1) {
      html.push('<p></p>');
    }
    pendingBlankLines = 0;
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    const headingMatch = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    const taskMatch = /^-\s\[( |x|X)\]\s+(.*)$/.exec(trimmed);
    const bulletMatch = /^[-*]\s+(.*)$/.exec(trimmed);
    const numberMatch = /^\d+\.\s+(.*)$/.exec(trimmed);

    if (trimmed === '') {
      flushAll();
      pendingBlankLines += 1;
      return;
    }

    flushPendingBlankParagraphs();

    if (headingMatch) {
      flushAll();
      const level = headingMatch[1].length;
      const content = escapeHtml(headingMatch[2] ?? '');
      html.push(`<h${level}>${content}</h${level}>`);
      return;
    }

    if (taskMatch) {
      flushParagraph();
      flushBullets();
      flushNumbered();
      taskItems.push({
        checked: taskMatch[1].toLowerCase() === 'x',
        value: taskMatch[2] ?? '',
      });
      return;
    }

    if (bulletMatch) {
      flushParagraph();
      flushNumbered();
      flushTasks();
      bulletItems.push(bulletMatch[1] ?? '');
      return;
    }

    if (numberMatch) {
      flushParagraph();
      flushBullets();
      flushTasks();
      numberedItems.push(numberMatch[1] ?? '');
      return;
    }

    flushBullets();
    flushNumbered();
    flushTasks();
    paragraphLines.push(line);
  });

  flushAll();
  flushPendingBlankParagraphs();
  return html.join('') || '<p></p>';
};

const htmlToMarkdown = (html: string): string => {
  if (!html.trim()) return '';
  if (typeof window === 'undefined') return html;

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const inlineToMarkdown = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent ?? '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    const children = Array.from(element.childNodes).map(inlineToMarkdown).join('');

    if (tag === 'strong' || tag === 'b') return `**${children}**`;
    if (tag === 'em' || tag === 'i') return `*${children}*`;
    if (tag === 'code') return `\`${children}\``;
    if (tag === 'br') return '\n';
    return children;
  };

  const blockToMarkdown = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? '').trim();
      return text ? `${text}\n\n` : '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();

    if (/^h[1-6]$/.test(tag)) {
      const level = Number(tag[1]);
      const content = inlineToMarkdown(element).trim();
      return content ? `${'#'.repeat(level)} ${content}\n\n` : '';
    }

    if (tag === 'p') {
      const content = inlineToMarkdown(element).trim();
      return content ? `${content}\n\n` : '\n';
    }

    if (tag === 'ul') {
      const isTaskList =
        element.getAttribute('data-type') === 'taskList' ||
        Array.from(element.children).some((child) => child.getAttribute('data-type') === 'taskItem');

      const lines = Array.from(element.children)
        .filter((child) => child.tagName.toLowerCase() === 'li')
        .map((child) => {
          const content = inlineToMarkdown(child).trim();
          if (!content) return '';
          if (isTaskList) {
            const checked = child.getAttribute('data-checked') === 'true';
            return `- [${checked ? 'x' : ' '}] ${content}`;
          }
          return `- ${content}`;
        })
        .filter(Boolean);

      return lines.length > 0 ? `${lines.join('\n')}\n\n` : '';
    }

    if (tag === 'ol') {
      const lines = Array.from(element.children)
        .filter((child) => child.tagName.toLowerCase() === 'li')
        .map((child, index) => {
          const content = inlineToMarkdown(child).trim();
          return content ? `${index + 1}. ${content}` : '';
        })
        .filter(Boolean);

      return lines.length > 0 ? `${lines.join('\n')}\n\n` : '';
    }

    const nested = Array.from(element.childNodes).map(blockToMarkdown).join('');
    return nested;
  };

  const raw = Array.from(doc.body.childNodes).map(blockToMarkdown).join('');
  return raw.replace(/\n{3,}/g, '\n\n').trim();
};

const editorDocToMarkdown = (doc: ProseMirrorNode): string => {
  const serializeInline = (node: ProseMirrorNode): string => {
    if (node.type.name === 'hardBreak') return '\n';
    if (!node.isText) {
      let nested = '';
      node.forEach((child) => {
        nested += serializeInline(child);
      });
      return nested;
    }

    let text = node.text ?? '';
    node.marks.forEach((mark) => {
      if (mark.type.name === 'code') text = `\`${text}\``;
      if (mark.type.name === 'italic') text = `*${text}*`;
      if (mark.type.name === 'bold') text = `**${text}**`;
    });
    return text;
  };

  const serializeBlock = (node: ProseMirrorNode, listDepth = 0): string => {
    if (node.type.name === 'paragraph') return serializeInline(node);
    if (node.type.name === 'heading') {
      const level = Number(node.attrs.level) || 1;
      return `${'#'.repeat(level)} ${serializeInline(node)}`;
    }
    if (node.type.name === 'bulletList' || node.type.name === 'orderedList' || node.type.name === 'taskList') {
      const lines: string[] = [];
      node.forEach((item, _offset, index) => {
        let content = '';
        item.forEach((child) => {
          const serialized = serializeBlock(child, listDepth + 1).trim();
          if (serialized) content += `${content ? '\n' : ''}${serialized}`;
        });
        if (!content) return;
        const indent = '  '.repeat(listDepth);
        if (node.type.name === 'taskList') {
          lines.push(`${indent}- [${item.attrs.checked ? 'x' : ' '}] ${content}`);
        } else if (node.type.name === 'orderedList') {
          lines.push(`${indent}${index + 1}. ${content}`);
        } else {
          lines.push(`${indent}- ${content}`);
        }
      });
      return lines.join('\n');
    }

    let nested = '';
    node.forEach((child) => {
      const serialized = serializeBlock(child, listDepth);
      if (serialized) nested += `${nested ? '\n\n' : ''}${serialized}`;
    });
    return nested;
  };

  const blocks: string[] = [];
  doc.forEach((node) => {
    if (node.type.name === 'paragraph' && node.content.size === 0) {
      blocks.push('');
      return;
    }

    const serialized = serializeBlock(node).trim();
    if (serialized) blocks.push(serialized);
  });
  return blocks.join('\n\n');
};

const toEditorContentHtml = (value: string): string => {
  if (!value.trim()) return '<p></p>';
  if (looksLikeHtml(value)) return value;
  return markdownToEditorHtml(value);
};

const resolveGermanQuote = (textBeforeCursor: string): string => {
  const previousCharacter = textBeforeCursor.slice(-1);
  const shouldOpen = !previousCharacter || /[\s([{<\u201e\u201c"'`]/.test(previousCharacter);
  return shouldOpen ? '\u201e' : '\u201c';
};

const isQuoteInput = (value: string): boolean =>
  value === '"' || value === '\u201e' || value === '\u201c' || value === '\u201d';

const MarkdownShortcutNotesEditor: React.FC<MarkdownShortcutNotesEditorProps> = ({
  value,
  onChange,
  immediateChanges = false,
  placeholder = "Use '# ' for headings, '-' or '*' for bullets, '1.' for numbered lists, and '[] ' for todos.",
  className,
  minHeightClassName = 'min-h-[170px]',
  disabled = false,
  documentKey = null,
  quoteStyle = 'straight',
  typewriterMode = 'off',
  onSelectionWordCountChange,
  analysisConfig = null,
  registerPendingChangeFlush,
  resizable = false,
  containerRef,
  style,
  initialPosition = null,
  onPositionChange,
}) => {
  const initialHtmlRef = useRef<string | null>(null);
  if (initialHtmlRef.current === null) {
    initialHtmlRef.current = toEditorContentHtml(value);
  }
  const lastEmittedMarkdownRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const immediateChangesRef = useRef(immediateChanges);
  immediateChangesRef.current = immediateChanges;
  const onSelectionWordCountChangeRef = useRef(onSelectionWordCountChange);
  const quoteStyleRef = useRef(quoteStyle);
  const typewriterModeRef = useRef(typewriterMode);
  const hasIgnoredWordMarkersRef = useRef(value.includes('++'));
  const typewriterFrameRef = useRef<number | null>(null);
  const typewriterScrollerRef = useRef<HTMLElement | null>(null);
  const pendingMarkdownEditorRef = useRef<Editor | null>(null);
  const pendingMarkdownTimeoutRef = useRef<number | null>(null);
  const pendingMarkdownMaxTimeoutRef = useRef<number | null>(null);
  const pendingMarkdownIdleCallbackRef = useRef<number | null>(null);
  const lastSyncedDocumentKeyRef = useRef<string | null>(documentKey);
  const analysisConfigRef = useRef(analysisConfig);
  const onPositionChangeRef = useRef(onPositionChange);
  const lastRestoredDocumentKeyRef = useRef<string | null>(null);
  const decorationCacheRef = useRef<{
    doc: ProseMirrorNode | null;
    analysisConfig: ManuscriptAnalysisConfig | null;
    hasIgnoredWordMarkers: boolean;
    decorations: DecorationSet | null;
  }>({
    doc: null,
    analysisConfig: null,
    hasIgnoredWordMarkers: false,
    decorations: null,
  });

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onSelectionWordCountChangeRef.current = onSelectionWordCountChange;
  }, [onSelectionWordCountChange]);

  useEffect(() => {
    quoteStyleRef.current = quoteStyle;
  }, [quoteStyle]);

  useEffect(() => {
    typewriterModeRef.current = typewriterMode;
  }, [typewriterMode]);

  useEffect(() => {
    onPositionChangeRef.current = onPositionChange;
  }, [onPositionChange]);

  const emitEditorMarkdown = useCallback((currentEditor: Editor) => {
    if (currentEditor.isDestroyed) return;
    const nextMarkdown = editorDocToMarkdown(currentEditor.state.doc);
    if (nextMarkdown === lastEmittedMarkdownRef.current) {
      return;
    }
    lastEmittedMarkdownRef.current = nextMarkdown;
    hasIgnoredWordMarkersRef.current = nextMarkdown.includes('++');
    onChangeRef.current(nextMarkdown);
  }, []);

  const flushPendingMarkdownChange = useCallback(() => {
    if (pendingMarkdownTimeoutRef.current !== null) {
      window.clearTimeout(pendingMarkdownTimeoutRef.current);
      pendingMarkdownTimeoutRef.current = null;
    }
    if (pendingMarkdownMaxTimeoutRef.current !== null) {
      window.clearTimeout(pendingMarkdownMaxTimeoutRef.current);
      pendingMarkdownMaxTimeoutRef.current = null;
    }
    if (pendingMarkdownIdleCallbackRef.current !== null) {
      const cancelIdleCallback = (window as Window & { cancelIdleCallback?: (handle: number) => void }).cancelIdleCallback;
      if (cancelIdleCallback) {
        cancelIdleCallback(pendingMarkdownIdleCallbackRef.current);
      } else {
        window.clearTimeout(pendingMarkdownIdleCallbackRef.current);
      }
      pendingMarkdownIdleCallbackRef.current = null;
    }

    const pendingEditor = pendingMarkdownEditorRef.current;
    pendingMarkdownEditorRef.current = null;
    if (pendingEditor) {
      emitEditorMarkdown(pendingEditor);
    }
  }, [emitEditorMarkdown]);

  useEffect(() => {
    registerPendingChangeFlush?.(flushPendingMarkdownChange);
    return () => registerPendingChangeFlush?.(null);
  }, [flushPendingMarkdownChange, registerPendingChangeFlush]);

  const scheduleMarkdownChange = (currentEditor: Editor) => {
    pendingMarkdownEditorRef.current = currentEditor;
    if (pendingMarkdownTimeoutRef.current !== null) {
      window.clearTimeout(pendingMarkdownTimeoutRef.current);
    }

    pendingMarkdownTimeoutRef.current = window.setTimeout(() => {
      pendingMarkdownTimeoutRef.current = null;
      const requestIdleCallback = (window as Window & {
        requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
      }).requestIdleCallback;

      pendingMarkdownIdleCallbackRef.current = requestIdleCallback
        ? requestIdleCallback(() => {
            pendingMarkdownIdleCallbackRef.current = null;
            flushPendingMarkdownChange();
          }, { timeout: 700 })
        : window.setTimeout(() => {
            pendingMarkdownIdleCallbackRef.current = null;
            flushPendingMarkdownChange();
          }, 0);
    }, 420);

    if (pendingMarkdownMaxTimeoutRef.current === null) {
      pendingMarkdownMaxTimeoutRef.current = window.setTimeout(() => {
        flushPendingMarkdownChange();
      }, 1600);
    }
  };

  const scheduleTypewriterScroll = () => {
    if (typewriterModeRef.current === 'off') return;
    if (typewriterFrameRef.current !== null) {
      return;
    }
    // Beim Markieren (ausgedehnte Selektion) NICHT typewriter-scrollen,
    // sonst rotiert der Text beim Hoch-/Runterziehen viel zu schnell durch.
    if (editor && !editor.state.selection.empty) {
      return;
    }

    typewriterFrameRef.current = window.requestAnimationFrame(() => {
      typewriterFrameRef.current = null;
      if (!editor?.isFocused) return;
      // Doppelter Guard: Selektion kann sich im rAF-Intervall geändert haben.
      if (!editor.state.selection.empty) return;


      let caret: { top: number; bottom: number };
      try {
        caret = editor.view.coordsAtPos(editor.state.selection.head);
      } catch {
        return;
      }

      const cachedScroller = typewriterScrollerRef.current;
      const scroller = cachedScroller?.isConnected ? cachedScroller : findEditorScrollContainer(editor.view.dom);
      typewriterScrollerRef.current = scroller;
      const scrollerRect = scroller.getBoundingClientRect();
      const caretCenter = (caret.top + caret.bottom) / 2;

      if (typewriterModeRef.current === 'centered') {
        const targetTop = scroller.scrollTop + caretCenter - scrollerRect.top - (scroller.clientHeight / 2);
        scroller.scrollTop = Math.max(0, targetTop);
        return;
      }

      const upperLine = scrollerRect.top + scroller.clientHeight * 0.46;
      const lowerLine = scrollerRect.top + scroller.clientHeight * 0.58;
      if (caretCenter < upperLine) {
        scroller.scrollTop -= upperLine - caretCenter;
      } else if (caretCenter > lowerLine) {
        scroller.scrollTop += caretCenter - lowerLine;
      }
    });
  };

  useEffect(() => {
    return () => {
      flushPendingMarkdownChange();
      if (typewriterFrameRef.current !== null) {
        window.cancelAnimationFrame(typewriterFrameRef.current);
        typewriterFrameRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const flushForPageExit = () => flushPendingMarkdownChange();
    window.addEventListener('pagehide', flushForPageExit);
    window.addEventListener('beforeunload', flushForPageExit);
    return () => {
      window.removeEventListener('pagehide', flushForPageExit);
      window.removeEventListener('beforeunload', flushForPageExit);
    };
  }, []);

  useEffect(() => {
    scheduleTypewriterScroll();
    typewriterScrollerRef.current = null;
  }, [typewriterMode]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      TaskList.configure({
        HTMLAttributes: {
          class: 'not-prose',
        },
      }),
      QuickNotesTaskItem.configure({
        nested: true,
      }),
      ListItemCycleShortcut,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: initialHtmlRef.current,
    editorProps: {
      attributes: {
        class:
          'h-full min-h-full w-full overflow-y-auto bg-transparent px-3 py-2 text-[15px] leading-6 outline-none',
        spellcheck: 'true',
      },
      decorations: (state) => (
        (() => {
          const config = analysisConfigRef.current;
          const hasIgnoredWordMarkers = hasIgnoredWordMarkersRef.current;
          const cached = decorationCacheRef.current;
          if (
            cached.decorations &&
            cached.doc === state.doc &&
            cached.analysisConfig === config &&
            cached.hasIgnoredWordMarkers === hasIgnoredWordMarkers
          ) {
            return cached.decorations;
          }

          const decorations = config
            ? buildAnalysisDecorations(state.doc, config)
            : hasIgnoredWordMarkers
              ? buildIgnoredWordCountDecorations(state.doc)
              : DecorationSet.empty;

          decorationCacheRef.current = {
            doc: state.doc,
            analysisConfig: config,
            hasIgnoredWordMarkers,
            decorations,
          };
          return decorations;
        })()
      ),
      handleKeyDown: (view, event) => {
        if (event.key === 'Backspace') {
          const { state } = view;
          const { selection, schema } = state;
          if (!selection.empty) return false;

          const { $from } = selection;
          const isAtLineStart = $from.parentOffset === 0;
          const isCurrentParagraph = $from.parent.type === schema.nodes.paragraph;
          const isCurrentParagraphEmpty = ($from.parent.textContent ?? '').trim().length === 0;
          if (!isAtLineStart || !isCurrentParagraph || !isCurrentParagraphEmpty) return false;
          if ($from.depth < 1) return false;

          const containerDepth = $from.depth - 1;
          const containerNode = $from.node(containerDepth);
          const currentIndex = $from.index(containerDepth);
          if (currentIndex <= 0) return false;

          const previousSibling = containerNode.child(currentIndex - 1);
          const isPreviousList =
            previousSibling.type === schema.nodes.bulletList ||
            previousSibling.type === schema.nodes.orderedList ||
            previousSibling.type === schema.nodes.taskList;
          if (!isPreviousList) return false;

          const previousSiblingPos = $from.posAtIndex(currentIndex - 1, containerDepth);
          const previousSiblingInnerEnd = previousSiblingPos + previousSibling.nodeSize - 1;

          const currentBlockStart = $from.before($from.depth);
          const currentBlockEnd = $from.after($from.depth);
          const tr = state.tr.delete(currentBlockStart, currentBlockEnd);
          const mappedEnd = Math.max(
            1,
            Math.min(tr.doc.content.size, tr.mapping.map(previousSiblingInnerEnd, -1))
          );
          const resolvedEnd = tr.doc.resolve(mappedEnd);
          tr.setSelection(Selection.near(resolvedEnd, -1));

          event.preventDefault();
          view.dispatch(tr.scrollIntoView());
          return true;
        }

        if (event.key !== ' ') return false;

        const { state } = view;
        const { selection, schema } = state;
        if (!selection.empty) return false;

        const { $from } = selection;
        const parent = $from.parent;
        if (!parent.isTextblock) return false;

        const lineText = parent.textBetween(0, $from.parentOffset, '', '').replace(/\u200b/g, '');
        const normalizedLineText = lineText.trim();
        if (normalizedLineText !== '[]' && normalizedLineText !== '[ ]') return false;
        if ($from.parentOffset !== parent.content.size) return false;

        const { taskList, taskItem, paragraph } = schema.nodes;
        if (!taskList || !taskItem || !paragraph) return false;

        const blockDepth = $from.depth;
        const blockFrom = $from.before(blockDepth);
        const blockTo = $from.after(blockDepth);

        const taskNode = taskList.create(null, [taskItem.create({ checked: false }, [paragraph.create()])]);
        const tr = state.tr.replaceWith(blockFrom, blockTo, taskNode);
        const mappedFrom = tr.mapping.map(blockFrom);
        const insertedTo = Math.min(tr.doc.content.size, mappedFrom + taskNode.nodeSize);

        let cursorPos = Math.max(1, Math.min(mappedFrom + 1, tr.doc.content.size));
        tr.doc.nodesBetween(mappedFrom, insertedTo, (node, pos) => {
          if (node.isTextblock) {
            cursorPos = pos + 1;
            return false;
          }
          return true;
        });
        cursorPos = Math.max(1, Math.min(cursorPos, tr.doc.content.size));
        tr.setSelection(TextSelection.create(tr.doc, cursorPos));

        event.preventDefault();
        view.dispatch(tr.scrollIntoView());
        return true;
      },
      handleTextInput: (view, from, to, text) => {
        if (quoteStyleRef.current !== 'german' || !isQuoteInput(text)) {
          return false;
        }

        const textBeforeCursor = view.state.doc.textBetween(Math.max(0, from - 2), from, '', '');
        const quote = resolveGermanQuote(textBeforeCursor);
        const tr = view.state.tr.insertText(quote, from, to);
        view.dispatch(tr.scrollIntoView());
        return true;
      },
    },
    editable: !disabled,
    onUpdate: ({ editor: currentEditor }) => {
      // Ignore programmatic updates (initial mount/setContent sync) to prevent
      // accidental autosave with transient values.
      if (!currentEditor.isFocused) {
        return;
      }

      if (immediateChangesRef.current) {
        emitEditorMarkdown(currentEditor);
      } else {
        scheduleMarkdownChange(currentEditor);
      }
      scheduleTypewriterScroll();
    },
    onBlur: () => {
      flushPendingMarkdownChange();
    },
    onSelectionUpdate: ({ editor: currentEditor }) => {
      // A mouse click or selection must never trigger typewriter scrolling.
      // The caret is repositioned only after an actual document update (typing).
      onPositionChangeRef.current?.({
        cursor: currentEditor.state.selection.head,
        scrollTop: findEditorScrollContainer(currentEditor.view.dom).scrollTop,
      });

      const callback = onSelectionWordCountChangeRef.current;
      if (!callback) return;
      const { from, to, empty } = currentEditor.state.selection;
      if (empty || from === to) {
        callback(0);
        return;
      }
      const selectedText = currentEditor.state.doc.textBetween(from, to, ' ', ' ');
      callback(countWordsForProgress(selectedText));
    },
  });

  useEffect(() => {
    analysisConfigRef.current = analysisConfig;
    editor?.view.dispatch(editor.state.tr.setMeta('analysis-refresh', true));
  }, [analysisConfig, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor || !initialPosition || lastRestoredDocumentKeyRef.current === documentKey) return;
    lastRestoredDocumentKeyRef.current = documentKey;
    const frame = window.requestAnimationFrame(() => {
      const cursor = Math.max(1, Math.min(Math.round(initialPosition.cursor), editor.state.doc.content.size));
      editor.commands.setTextSelection(cursor);
      const scroller = findEditorScrollContainer(editor.view.dom);
      scroller.scrollTop = Math.max(0, initialPosition.scrollTop);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [documentKey, editor, initialPosition]);

  useEffect(() => {
    if (!editor || !onPositionChange) return;
    const scroller = findEditorScrollContainer(editor.view.dom);
    let frame: number | null = null;
    const emitPosition = () => {
      frame = null;
      onPositionChangeRef.current?.({
        cursor: editor.state.selection.head,
        scrollTop: scroller.scrollTop,
      });
    };
    const schedulePosition = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(emitPosition);
    };
    scroller.addEventListener('scroll', schedulePosition, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', schedulePosition);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [editor, onPositionChange]);

  useEffect(() => {
    if (!editor) return;
    const documentChanged = documentKey !== lastSyncedDocumentKeyRef.current;

    if (!documentChanged && editor.isFocused) {
      flushPendingMarkdownChange();
      const currentMarkdown = editorDocToMarkdown(editor.state.doc);
      if (currentMarkdown !== value) {
        if (currentMarkdown !== lastEmittedMarkdownRef.current) {
          lastEmittedMarkdownRef.current = currentMarkdown;
          hasIgnoredWordMarkersRef.current = currentMarkdown.includes('++');
          onChangeRef.current(currentMarkdown);
        }
        return;
      }
    }

    if (!documentChanged && value === lastEmittedMarkdownRef.current) {
      lastSyncedDocumentKeyRef.current = documentKey;
      return;
    }
    typewriterScrollerRef.current = null;
    const wasFocused = editor.isFocused;
    const cursorPosition = editor.state.selection.from;
    // Keep emitted baseline in sync with externally loaded values.
    // Otherwise deleting back to empty right after load can be treated as "no change".
    lastEmittedMarkdownRef.current = value;
    lastSyncedDocumentKeyRef.current = documentKey;
    hasIgnoredWordMarkersRef.current = value.includes('++');
    replaceEditorContent(editor, toEditorContentHtml(value));
    if (wasFocused) {
      editor.commands.setTextSelection(Math.min(cursorPosition, editor.state.doc.content.size));
    }
  }, [documentKey, editor, value]);

  return (
    <div
      ref={containerRef}
      style={style}
      onPointerDown={(event) => {
        if (resizable) {
          event.stopPropagation();
        }
      }}
      onMouseDown={(event) => {
        if (disabled || !editor) return;
        if (resizable) {
          const bounds = event.currentTarget.getBoundingClientRect();
          const isResizeHandle =
            event.clientX >= bounds.right - 20 &&
            event.clientY >= bounds.bottom - 20;
          if (isResizeHandle) {
            return;
          }
        }
        const target = event.target as HTMLElement;
        if (target.closest('.ProseMirror')) {
          // Let ProseMirror place the caret exactly at click position.
          return;
        }
        event.preventDefault();
        // Clicked the surrounding empty area: place caret at text end.
        editor.chain().focus('end').run();
      }}
      className={cn(
        'quick-notes-editor cursor-text overflow-hidden rounded-md border border-input bg-transparent',
        resizable && 'resize-y overflow-auto',
        minHeightClassName,
        '[&_.ProseMirror_h1]:text-[1.45em] [&_.ProseMirror_h1]:font-semibold [&_.ProseMirror_h1]:leading-tight',
        '[&_.ProseMirror_h2]:text-[1.28em] [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:leading-tight',
        '[&_.ProseMirror_h3]:text-[1.14em] [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:leading-snug',
        '[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none',
        '[&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left',
        '[&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0',
        '[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground',
        '[&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
        '[&_.ProseMirror_li]:pl-0 [&_.ProseMirror_li_p]:my-0',
        '[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-3 [&_.ProseMirror_ol_li]:pl-0',
        '[&_.ns-ignored-word-count-range]:text-muted-foreground/55 [&_.ns-ignored-word-count-range]:bg-muted/35 [&_.ns-ignored-word-count-range]:rounded-sm',
        '[&_.ns-analysis-highlight]:rounded-[2px] [&_.ns-analysis-adverbs]:bg-amber-300/45 [&_.ns-analysis-fillerWords]:bg-rose-300/40 [&_.ns-analysis-weakVerbs]:bg-blue-300/40 [&_.ns-analysis-tellIndicators]:bg-violet-300/40 [&_.ns-analysis-cliches]:bg-orange-300/40 [&_.ns-analysis-pacingWeakeners]:bg-teal-300/40 [&_.ns-analysis-redundancies]:bg-pink-300/40',
        '[&_.ProseMirror]:h-full [&_.ProseMirror]:min-h-[inherit]',
        className
      )}
    >
      <EditorContent
        editor={editor}
        className={cn('h-full [&_.ProseMirror]:outline-none')}
      />
    </div>
  );
};

export default MarkdownShortcutNotesEditor;
